import { useMemo, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

/**
 * DateField — drop-in replacement for `<input type="date" class="field-input">`.
 *
 * Uses shadcn Calendar + Popover so the picker matches the warm-earth theme
 * in both light and dark modes, and works consistently across mobile browsers
 * (native date pickers on iOS/Android are inconsistent).
 *
 * Contract intentionally mirrors an <input>:
 *   - `value`      : ISO string "YYYY-MM-DD" (never null; empty string = unset)
 *   - `onChange`   : receives ONE argument — the ISO date string
 *   - `min`/`max`  : optional ISO date strings
 *   - `disabled`   : boolean
 *   - `placeholder`: label to render when value is empty
 *   - `testid`     : optional test-id for the trigger button
 *
 * We keep the trigger visually identical to `.field-input` so no downstream
 * layout tweaks are needed.
 */
export default function DateField({
  value,
  onChange,
  min,
  max,
  disabled = false,
  placeholder = "Select date",
  testid,
  ariaLabel,
}) {
  const [open, setOpen] = useState(false);

  // Convert ISO string ↔ Date safely. Empty/invalid → undefined so the calendar
  // shows no selection instead of crashing on "Invalid Date".
  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  const label = useMemo(() => {
    if (!selectedDate) return placeholder;
    return selectedDate.toLocaleDateString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
    });
  }, [selectedDate, placeholder]);

  const toISO = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const disabledMatcher = (day) => {
    if (min) {
      const minDate = new Date(`${min}T00:00:00`);
      if (day < minDate) return true;
    }
    if (max) {
      const maxDate = new Date(`${max}T00:00:00`);
      if (day > maxDate) return true;
    }
    return false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          aria-label={ariaLabel || placeholder}
          className="field-input text-left flex items-center justify-between gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <span className={selectedDate ? "text-[var(--text-primary)]" : "text-[var(--text-muted)]"}>
            {label}
          </span>
          <CalendarIcon size={15} className="text-[var(--text-muted)] flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 border-[var(--border-default)] bg-[var(--bg-card)]"
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => {
            onChange?.(toISO(d));
            setOpen(false);
          }}
          disabled={disabledMatcher}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
